import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Divider, Grid, makeStyles } from '@material-ui/core'
import FlexRow from '../../../../../../molecules/FlexRow'
import SydButton from '../../../../../../commonDesign/Button'
import CodeBlock from '../../../../../../atoms/CodeBlock'

const useStyles = makeStyles((theme) => ({
  variablesPanel: {
    width: '100%',
    height: '100%',
    backgroundColor: 'var(--theme-color-surface-d3)',
    borderLeft: 'var(--border-neutral-thin)',
    boxSizing: 'border-box',
    padding: theme.layout.padding.p5
  }
}))

function tryParseJson (str) {
  if (!str) return null
  try {
    return JSON.parse(str)
  } catch {
    return str
  }
}

function toFilterRows (variables = {}) {
  return Object.entries(variables?.filters || {}).map(([k, v]) => ({
    id: Math.random().toString(),
    name: k,
    value: JSON.stringify(v)
  }))
}

function toDateRows (variables = {}) {
  return Object.entries(variables?.dates || {}).map(([k, v]) => ({
    id: Math.random().toString(),
    name: k,
    startDate: JSON.stringify(v.startDate),
    endDate: JSON.stringify(v.endDate)
  }))
}

function deriveVariables (filterRows, dateRows) {
  return {
    filters: filterRows.reduce((p, c) => {
      if (c.name) p[c.name] = tryParseJson(c.value)
      return p
    }, {}),
    dates: dateRows.reduce((p, c) => {
      if (c.name) {
        p[c.name] = {
          startDate: tryParseJson(c.startDate),
          endDate: tryParseJson(c.endDate)
        }
      }
      return p
    }, {})
  }
}

function Variables ({ defaultVariables, onChange }) {
  const classes = useStyles()
  const [filterVariables, setFilterVariables] = useState(() => toFilterRows(defaultVariables))
  const [dateVariables, setDateVariables] = useState(() => toDateRows(defaultVariables))

  const prevDefaultRef = useRef(defaultVariables)
  const suppressSyncRef = useRef(false)
  const isMountRef = useRef(true)

  useEffect(() => {
    if (suppressSyncRef.current) {
      suppressSyncRef.current = false
      prevDefaultRef.current = defaultVariables
      return
    }
    if (prevDefaultRef.current !== defaultVariables) {
      prevDefaultRef.current = defaultVariables
      setFilterVariables(toFilterRows(defaultVariables))
      setDateVariables(toDateRows(defaultVariables))
    }
  }, [defaultVariables])

  const displayValue = useMemo(
    () => deriveVariables(filterVariables, dateVariables),
    [filterVariables, dateVariables]
  )

  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false
      return
    }
    suppressSyncRef.current = true
    onChange?.(displayValue)
  }, [displayValue, onChange])

  const addFilterVariable = useCallback(() => {
    setFilterVariables(p => [...p, { id: Math.random().toString(), name: '', value: '' }])
  }, [])

  const addDateVariable = useCallback(() => {
    setDateVariables(p => [...p, { id: Math.random().toString(), name: '', startDate: '', endDate: '' }])
  }, [])

  const changeFilterVariable = useCallback((id, field) => (e) => {
    const val = e.target.value
    setFilterVariables(p => p.map(item => item.id === id ? { ...item, [field]: val } : item))
  }, [])

  const changeDateVariable = useCallback((id, field) => (e) => {
    const val = e.target.value
    setDateVariables(p => p.map(item => item.id === id ? { ...item, [field]: val } : item))
  }, [])

  const removeFilterVariable = useCallback((id) => () => {
    setFilterVariables(p => p.filter(x => x.id !== id))
  }, [])

  const removeDateVariable = useCallback((id) => () => {
    setDateVariables(p => p.filter(x => x.id !== id))
  }, [])

  return (
    <div className={classes.variablesPanel}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <FlexRow>
            <div>Filter Variables</div>
            <SydButton size='xs' icon='add' onClick={addFilterVariable}>Add</SydButton>
          </FlexRow>
        </Grid>
        {filterVariables.map(fv => (
          <Grid item xs={12} key={fv.id}>
            <FlexRow>
              <div style={{ flex: 1 }}>
                <input placeholder='variable name' value={fv.name} onChange={changeFilterVariable(fv.id, 'name')} style={{ width: '100%' }} />
              </div>
              <div style={{ flex: 1 }}>
                <input placeholder='variable value' value={fv.value} onChange={changeFilterVariable(fv.id, 'value')} style={{ width: '100%' }} />
              </div>
              <div>
                <SydButton size='xs' icon='close' onClick={removeFilterVariable(fv.id)}>Remove</SydButton>
              </div>
            </FlexRow>
          </Grid>
        ))}
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <FlexRow>
            <div>Date Variables</div>
            <SydButton size='xs' icon='add' onClick={addDateVariable}>Add</SydButton>
          </FlexRow>
        </Grid>
        {dateVariables.map(dv => (
          <Grid item xs={12} key={dv.id}>
            <FlexRow>
              <div style={{ flex: 1 }}>
                <input placeholder='variable name' value={dv.name} onChange={changeDateVariable(dv.id, 'name')} style={{ width: '100%' }} />
              </div>
              <div style={{ flex: 1 }}>
                <input placeholder='start' value={dv.startDate} onChange={changeDateVariable(dv.id, 'startDate')} style={{ width: '100%' }} />
              </div>
              <div style={{ flex: 1 }}>
                <input placeholder='end' value={dv.endDate} onChange={changeDateVariable(dv.id, 'endDate')} style={{ width: '100%' }} />
              </div>
              <div>
                <SydButton size='xs' icon='close' onClick={removeDateVariable(dv.id)}>Remove</SydButton>
              </div>
            </FlexRow>
          </Grid>
        ))}
        <Grid item xs={12}>
          <CodeBlock code={displayValue} scroll />
        </Grid>
      </Grid>
    </div>
  )
}

Variables.propTypes = {
  defaultVariables: PropTypes.object,
  onChange: PropTypes.func
}

export default Variables
